import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { KhisSubmissionView, SessionView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  TextArea,
} from '@wodi/ui';
import { useState } from 'react';

import {
  approveKhisSubmission,
  discardKhisSubmission,
  khisKeys,
  khisSubmissionQuery,
  sendKhisSubmissionAgain,
} from '../api/khis.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { toasts } from '../toasts.ts';
import { errorText, LoadFailed, periodWords, SubmissionTag, whenWords } from './format.tsx';
import styles from './khis.module.css';

// One month's KHIS figures (docs/plans/phase3-khis-and-quality.md §4): the frozen values,
// who prepared and approved them, the state with the reason and next step when sending
// failed, and the actions the reader may take. Counts only: no patient is named here.

function stateLine(s: KhisSubmissionView): string {
  switch (s.state) {
    case 'prepared':
      return s.preparedByReader
        ? 'You prepared these figures. Someone else checks and approves them before they are sent.'
        : 'Check the figures. Approving sends them to KHIS.';
    case 'waiting':
      return 'Approved. Wodi is sending these figures to KHIS.';
    case 'sent':
      return 'KHIS has these figures.';
    case 'failed':
      return 'These figures were approved but not sent.';
    case 'discarded':
      return 'These figures were discarded and will not be sent.';
    case 'superseded':
      return 'A correction for this month replaced these figures.';
  }
}

function Summary({ s }: { s: KhisSubmissionView }) {
  const summary = s.importSummary;
  return (
    <dl className={styles.facts}>
      <dt>Facility</dt>
      <dd>{s.facility.name}</dd>
      <dt>Organisation unit</dt>
      <dd className={styles.uid}>{s.orgUnitUid}</dd>
      <dt>Data set</dt>
      <dd className={styles.uid}>{s.dataSetUid}</dd>
      <dt>Mapping</dt>
      <dd>{`Version ${String(s.mappingVersion)}`}</dd>
      <dt>Prepared</dt>
      <dd>{`${whenWords(s.preparedAt)} by ${s.preparedBy.displayName}`}</dd>
      <dt>Approved</dt>
      <dd>
        {s.approvedAt === null || s.approvedBy === null
          ? 'Not yet'
          : `${whenWords(s.approvedAt)} by ${s.approvedBy.displayName}`}
      </dd>
      {s.correctionReason === null ? null : (
        <>
          <dt>Correction</dt>
          <dd>{s.correctionReason}</dd>
        </>
      )}
      {s.discardReason === null ? null : (
        <>
          <dt>Why discarded</dt>
          <dd>{s.discardReason}</dd>
        </>
      )}
      {summary === null ? null : (
        <>
          <dt>KHIS stored</dt>
          <dd>
            {`${String(summary.imported)} new, ${String(summary.updated)} updated, ${String(summary.ignored)} refused`}
            {summary.reference === null ? null : (
              <>
                {', reference '}
                <span className={styles.uid}>{summary.reference}</span>
              </>
            )}
          </dd>
        </>
      )}
    </dl>
  );
}

function Values({ s }: { s: KhisSubmissionView }) {
  const unknownAges = s.values.reduce((n, v) => Math.max(n, v.unknownAgeCount), 0);
  return (
    <Section
      title="Figures"
      description="Each count goes to the data element and category option combo beside it."
    >
      {unknownAges > 0 ? (
        <p className={styles.prose}>
          Rows with an age band leave out patients with no date of birth; the last column says how
          many. Recording dates of birth at registration makes these counts complete.
        </p>
      ) : null}
      <TableScroll>
        <Table flat aria-label="KHIS figures">
          <TableHeader>
            <Column isRowHeader>Row</Column>
            <Column>Data element</Column>
            <Column>Category option combo</Column>
            <Column>Count</Column>
            <Column>No date of birth</Column>
          </TableHeader>
          <TableBody items={s.values.map((v, i) => ({ ...v, id: String(i) }))}>
            {(v) => (
              <Row id={v.id}>
                <Cell>{v.label}</Cell>
                <Cell>
                  <span className={styles.uid}>{v.dataElementUid}</span>
                </Cell>
                <Cell>
                  <span className={styles.uid}>{v.categoryOptionComboUid}</span>
                </Cell>
                <Cell>
                  <span className={styles.numeric}>{v.value.toLocaleString('en-KE')}</span>
                </Cell>
                <Cell>
                  <span className={styles.numeric}>{String(v.unknownAgeCount)}</span>
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      </TableScroll>
    </Section>
  );
}

function Actions({ s, session }: { s: KhisSubmissionView; session: SessionView }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const [discarding, setDiscarding] = useState(false);
  const [reason, setReason] = useState('');
  const done = async (next: KhisSubmissionView, title: string) => {
    queryClient.setQueryData(khisKeys.submission(next.id), next);
    await queryClient.invalidateQueries({ queryKey: khisKeys.submissions });
    toasts.show({ title });
  };
  const approve = useMutation({
    mutationFn: () => approveKhisSubmission(s.id),
    onSuccess: (next) => done(next, 'Figures approved'),
  });
  const again = useMutation({
    mutationFn: () => sendKhisSubmissionAgain(s.id),
    onSuccess: (next) => done(next, 'Sending again'),
  });
  const discard = useMutation({
    mutationFn: () => discardKhisSubmission(s.id, reason.trim()),
    onSuccess: async (next) => {
      setDiscarding(false);
      await done(next, 'Figures discarded');
    },
  });
  const canApprove =
    s.state === 'prepared' && !s.preparedByReader && can(session, 'khis.submission.approve');
  const canDiscard = s.state === 'prepared' && can(session, 'khis.submission.prepare');
  const canSendAgain = s.state === 'failed' && can(session, 'khis.submission.approve');
  const error = approve.error ?? again.error;
  if (!canApprove && !canDiscard && !canSendAgain) return null;
  return (
    <>
      <div className={styles.actions}>
        {canApprove ? (
          <Button
            variant="primary"
            isDisabled={!online || approve.isPending}
            onPress={() => {
              approve.mutate();
            }}
          >
            Approve and send
          </Button>
        ) : null}
        {canSendAgain ? (
          <Button
            variant="primary"
            isDisabled={!online || again.isPending}
            onPress={() => {
              again.mutate();
            }}
          >
            Send again
          </Button>
        ) : null}
        {canDiscard ? (
          <Button
            isDisabled={!online}
            onPress={() => {
              setDiscarding(true);
            }}
          >
            Discard
          </Button>
        ) : null}
      </div>
      {error === null ? null : (
        <Notice tone="error">{errorText(error, 'That did not work. Try again.')}</Notice>
      )}
      <Dialog title="Discard these figures" isOpen={discarding} onOpenChange={setDiscarding}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim().length >= 3) discard.mutate();
          }}
        >
          <TextArea label="Reason" value={reason} onChange={setReason} />
          {discard.error === null ? null : (
            <Notice tone="error">{errorText(discard.error, 'Not discarded. Try again.')}</Notice>
          )}
          <DialogActions>
            <Button
              variant="quiet"
              onPress={() => {
                setDiscarding(false);
              }}
            >
              Keep them
            </Button>
            <Button
              type="submit"
              variant="primary"
              isDisabled={!online || discard.isPending || reason.trim().length < 3}
            >
              Discard
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

export function KhisSubmissionPage({ submissionId }: { submissionId: string }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'khis.read');
  const query = useQuery({ ...khisSubmissionQuery(submissionId), enabled: allowed });
  const online = useOnline();
  const s = query.data;
  return (
    <div className={styles.page}>
      <Link to="/reports/khis">All KHIS submissions</Link>
      <PageHeader
        title={s === undefined ? 'KHIS submission' : `KHIS figures for ${periodWords(s.period)}`}
      />
      {session === null || !allowed ? (
        <Notice tone="info">
          You don&apos;t have permission to see KHIS submissions. Ask an administrator if you need
          it.
        </Notice>
      ) : null}
      {online ? null : (
        <Notice tone="info">
          You&apos;re offline. This shows what was last loaded; approving needs the connection.
        </Notice>
      )}
      {query.isPending && allowed ? <LoadingBlock label="Loading the figures" rows={5} /> : null}
      {query.isError ? (
        <LoadFailed error={query.error} what="The figures" retry={() => void query.refetch()} />
      ) : null}
      {s === undefined || session === null ? null : (
        <>
          <Section title="State" actions={<SubmissionTag state={s.state} />}>
            <p className={styles.prose}>{stateLine(s)}</p>
            {s.failure === null ? null : (
              <Notice tone="error">{`${s.failure.message} ${s.failure.nextStep}`}</Notice>
            )}
            {s.importSummary !== null && s.importSummary.refusedDataElements.length > 0 ? (
              <p className={styles.prose}>
                {'KHIS refused values for data elements '}
                <span className={styles.uid}>{s.importSummary.refusedDataElements.join(', ')}</span>
                .
              </p>
            ) : null}
            <Actions s={s} session={session} />
            <Summary s={s} />
          </Section>
          <Values s={s} />
        </>
      )}
    </div>
  );
}
